import React from 'react'

import PropTypes from 'prop-types'

import { Box } from 'app/components/design-system-components'
import {
  disclosureVersionedQuestions,
  getDisclosureLatestVersion,
  getDisclosureMissingVersion,
} from 'app/pages/teams/onlist/components/Disclosures/questions'

// Helper function to check if conditional question should be displayed
const shouldShowConditionalQuestion = (question, answers) => {
  if (!question.conditionalOn) {
    return true
  }
  const conditionalValue = answers[question.conditionalOn]
  // Support explicit conditionalOnValue (e.g., show when parent is false)
  if (question.conditionalOnValue !== undefined) {
    return conditionalValue === question.conditionalOnValue
  }
  // Default: show when parent value is true
  return conditionalValue === true
}

// Helper function to determine the appropriate onChange handler for a question
const getOnChangeHandler = (questionKey) => {
  // Text input questions that require label parameter (embedded network fields)
  const textInputWithLabelQuestions = [
    'embeddedNetworkName',
    'embeddedNetworkAbn',
    'embeddedNetworkPhone',
    'embeddedNetworkWebsite',
    'embeddedNetworkPricing',
  ]

  // Text input questions that handle label internally (description fields)
  const textInputNoLabelQuestions = ['further_information']

  // Date input questions
  const dateInputQuestions = [
    'dateOfHomicide',
    'dateOfDrugUsage',
    'dateOfRepairNotice',
    'dateOfGasCheck',
    'dateOfElectricityCheck',
    'dateOfPoolCheck',
  ]

  // File upload questions
  const fileUploadQuestions = ['owners_corporation_rules_attachment']

  if (textInputWithLabelQuestions.includes(questionKey)) {
    return 'textInputWithLabel'
  }
  if (textInputNoLabelQuestions.includes(questionKey)) {
    return 'textInputNoLabel'
  }
  if (dateInputQuestions.includes(questionKey)) {
    return 'updateDateInput'
  }
  if (fileUploadQuestions.includes(questionKey)) {
    return 'fileUpload'
  }
  return 'onChangeRadioGroup'
}

// Helper function to get the label for text input questions (embedded network fields only)
const getQuestionLabel = (questionKey) => {
  const labels = {
    embeddedNetworkName: 'Embedded Network Name',
    embeddedNetworkAbn: 'ABN',
    embeddedNetworkPhone: 'Telephone',
    embeddedNetworkWebsite: 'Website Link',
    embeddedNetworkPricing: 'Pricing Link',
  }
  return labels[questionKey] || ''
}

const DisclosureQuestions = ({
  answers,
  onChangeRadioGroup,
  updateDateInput,
  inputValueChange,
  onFileUpload,
  onFileDelete,
  uploadingFiles,
  uploadErrors,
  version,
  areDisclosuresEditable,
  disclosureErrors = {},
}) => {
  if (!version) {
    // if version is missing: In editing (creating a disclosure), use the latest version, while in displaying assume the oldest one.
    version = areDisclosuresEditable
      ? getDisclosureLatestVersion()
      : getDisclosureMissingVersion()
  }

  if (!answers) return <></>

  // Define question rendering order matching Victorian disclosure statement
  const questionOrder = [
    // Q1: Property Sale
    'isBeingSold',
    'has_engaged_agent_to_sell',
    'has_contract_of_sale_prepared',
    // Q2: Mortgagee Action
    'has_mortgagee_action',
    'mortgagee_has_commenced_proceedings',
    // Q3: Property Ownership
    'is_property_owner',
    'has_right_to_rent',
    // Q4: Embedded Network
    'hasEmbeddedNetwork',
    'embeddedNetworkName',
    'embeddedNetworkAbn',
    'embeddedNetworkPhone',
    'embeddedNetworkWebsite',
    'embeddedNetworkPricing',
    // Q5: Homicide
    'hasHomicide',
    'dateOfHomicide',
    // Q6: Registered Place (V2 only)
    'isRegisteredPlace',
    // Q7: Rental Minimum Standards
    'compliesWithMinimumStandards',
    // Q8: Mould/Damp Repair Notice
    'dateOfRepairNotice',
    'has_mould_damp_repair_notice_3yr',
    // Q9: Electricity Safety Check
    'dateOfElectricityCheck',
    // Q10: Gas Safety Check
    'dateOfGasCheck',
    // Q11: Pool Barrier Check
    'dateOfPoolCheck',
    // Q12: Outstanding Safety Recommendations
    'has_outstanding_safety_recommendations',
    // Q13: Heritage Listed
    'is_heritage_listed',
    // Q14: Drug Usage
    'dateOfDrugUsage',
    'has_drug_usage_5yr',
    // Q15: Asbestos
    'hasAsbestos',
    // Q16: Building/Planning Application
    'isAffectedWithRelevantAuthorities',
    // Q17: Building Defects/Safety Concerns
    'has_defect_safety_concern_notice',
    // Q18: Domestic Building Dispute
    'has_domestic_building_dispute',
    // Q19: Owners Corporation Dispute
    'has_owners_corporation_dispute',
    // Q20: Owners Corporation Rules
    'has_owners_corporation_rules',
    'owners_corporation_rules_attachment',
    // General notes field (always visible)
    'further_information',
  ]

  return (
    <Box>
      {questionOrder.map((questionKey) => {
        const question = disclosureVersionedQuestions[questionKey]

        // Skip if question doesn't exist
        if (!question) {
          return null
        }

        // Check if question should be displayed for this version
        if (!question.versions.includes(version)) {
          return null
        }

        // Check conditional display logic
        if (!shouldShowConditionalQuestion(question, answers)) {
          return null
        }

        // Determine the appropriate onChange handler and build render parameters
        const handlerType = getOnChangeHandler(questionKey)
        let renderParams

        if (handlerType === 'textInputWithLabel') {
          // Embedded network fields: render(value, err, isEditable, label, onChange, version)
          const label = getQuestionLabel(questionKey)
          renderParams = [
            answers[questionKey],
            disclosureErrors[questionKey],
            areDisclosuresEditable,
            label,
            inputValueChange,
            version,
          ]
        } else if (handlerType === 'textInputNoLabel') {
          // Description fields: render(value, err, isEditable, onChange, version)
          renderParams = [
            answers[questionKey],
            disclosureErrors[questionKey],
            areDisclosuresEditable,
            inputValueChange,
            version,
          ]
        } else if (handlerType === 'updateDateInput') {
          // Date fields: render(value, err, isEditable, onChange, version)
          renderParams = [
            answers[questionKey],
            disclosureErrors[questionKey],
            areDisclosuresEditable,
            updateDateInput,
            version,
          ]
        } else if (handlerType === 'fileUpload') {
          // File upload fields: render(value, err, isEditable, id, onFileUpload, uploading, uploadError, onFileDelete)
          renderParams = [
            answers[questionKey],
            disclosureErrors[questionKey],
            areDisclosuresEditable,
            questionKey,
            onFileUpload,
            uploadingFiles[questionKey] || false,
            uploadErrors[questionKey] || null,
            onFileDelete,
          ]
        } else {
          // Boolean fields: render(value, err, isEditable, onChange, version)
          renderParams = [
            answers[questionKey],
            disclosureErrors[questionKey],
            areDisclosuresEditable,
            onChangeRadioGroup,
            version,
          ]
        }

        // Render the question
        return (
          <Box key={questionKey} mb={6}>
            {question.render(...renderParams)}
          </Box>
        )
      })}
    </Box>
  )
}

DisclosureQuestions.propTypes = {
  answers: PropTypes.object,
  onChangeRadioGroup: PropTypes.func,
  updateDateInput: PropTypes.func,
  inputValueChange: PropTypes.func,
  onFileUpload: PropTypes.func,
  onFileDelete: PropTypes.func,
  uploadingFiles: PropTypes.object,
  uploadErrors: PropTypes.object,
  version: PropTypes.number,
  areDisclosuresEditable: PropTypes.bool,
  disclosureErrors: PropTypes.object,
}

DisclosureQuestions.defaultProps = {
  onChangeRadioGroup: () => {},
  updateDateInput: () => {},
  inputValueChange: () => () => {},
  onFileUpload: () => {},
  onFileDelete: () => {},
  uploadingFiles: {},
  uploadErrors: {},
}

export default DisclosureQuestions
